import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { useLanguage } from '../hooks/useLanguage';
import { useScrollReveal } from '../hooks/useScrollReveal';
export function AboutPage() {
  const {
    t,
    setLocale,
    direction
  } = useLanguage();
  const location = useLocation();
  const {
    ref: heroRef,
    isVisible: heroVisible
  } = useScrollReveal();
  const {
    ref: descRef,
    isVisible: descVisible
  } = useScrollReveal();
  const {
    ref: quoteRef,
    isVisible: quoteVisible
  } = useScrollReveal();
  const {
    ref: missionRef,
    isVisible: missionVisible
  } = useScrollReveal();
  const {
    ref: visionRef,
    isVisible: visionVisible
  } = useScrollReveal();
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useScrollReveal();
  useEffect(() => {
    if (location.pathname.startsWith('/ar')) {
      setLocale('ar');
    } else {
      setLocale('en');
    }
    window.scrollTo(0, 0);
  }, [location.pathname, setLocale]);
  return <div className="min-h-screen w-full bg-white text-slate-900" dir={direction} data-id="element-250">
      <Header data-id="element-251" />

      <main data-id="element-252">
        {/* 1. Hero Banner */}
        <section className="relative min-h-screen overflow-hidden bg-[#031458]" ref={heroRef} data-id="element-253">
          {/* Background Image */}
          <div className="absolute inset-0 bg-cover bg-center" style={{
          backgroundImage: "url('https://cdn.magicpatterns.com/uploads/phze1e2RnA5DikoafWfyEv/about-hero-bg.jpg')",
          opacity: 0.2,
          mixBlendMode: 'luminosity'
        }} data-id="element-254" />
          <div className="absolute inset-0 bg-[#031458]/30" data-id="element-255" />

          {/* Decorative Curve */}
          <img src="https://cdn.magicpatterns.com/uploads/aVLvkvrFLX79R8UEYVhXQZ/about-circle.svg" alt="" className="absolute top-0 right-0 w-full h-auto pointer-events-none opacity-30" data-id="element-256" />

          <div className="relative z-10 w-full min-h-screen flex items-center" data-id="element-257">
            <div className="container mx-auto px-6 md:px-12" data-id="element-258">
              <div className="text-white max-w-3xl" data-id="element-259">
                <h1 className={`font-semibold leading-[1.05] mb-10 uppercase text-[32px] md:text-[44px] lg:text-[55px] reveal-hidden ${heroVisible ? 'animate-fade-up' : ''}`} data-id="element-260">
                  {t('aboutPage.hero.title')}
                </h1>
                <p className={`text-base md:text-lg text-white mb-10 max-w-lg leading-relaxed reveal-hidden ${heroVisible ? 'animate-fade-up delay-200' : ''}`} data-id="element-261">
                  {t('aboutPage.hero.subtitle')}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* 2. Description Section */}
        <section className="py-24 bg-[#061D76] text-white relative overflow-hidden" ref={descRef} data-id="element-262">
          <div className="container mx-auto px-6 md:px-12 relative z-10" data-id="element-263">
            <div className="max-w-4xl mx-auto space-y-8 text-lg md:text-xl leading-relaxed font-light" data-id="element-264">
              <p className={`reveal-hidden ${descVisible ? 'animate-fade-up' : ''}`} data-id="element-265">
                {t('aboutPage.description.paragraph1')}
              </p>
              <p className={`reveal-hidden ${descVisible ? 'animate-fade-up delay-200' : ''}`} data-id="element-266">
                {t('aboutPage.description.paragraph2')}
              </p>
            </div>
          </div>
        </section>

        {/* 3. Team Strength Quote */}
        <section className="py-32 md:py-44 relative overflow-hidden" style={{
        background: 'linear-gradient(to bottom, #DEEFF2, #FFFFFF)'
      }} ref={quoteRef} data-id="element-267">
          {/* Background decoration */}
          <img src="https://cdn.magicpatterns.com/uploads/aRzhnATJxjnpJmo17BGEmX/5.svg" alt="" className="absolute bottom-0 right-0 w-[400px] h-auto pointer-events-none opacity-50" data-id="element-268" />

          <div className="container mx-auto px-6 md:px-12 relative z-10" data-id="element-269">
            <h2 className={`text-2xl md:text-4xl font-medium text-[#061d76] !leading-[1.5] max-w-5xl reveal-hidden ${quoteVisible ? 'animate-fade-up' : ''}`} data-id="element-270">
              {t('aboutPage.teamQuote')}
            </h2>
          </div>
        </section>

        {/* 4. Our Mission */}
        <section className="py-24 text-white relative overflow-hidden" style={{
        background: 'linear-gradient(to right, #061D76, #25B8D6)'
      }} ref={missionRef} data-id="element-271">
          <div className="container mx-auto px-6 md:px-12 relative z-10" data-id="element-272">
            <div className="grid md:grid-cols-12 gap-8 md:gap-12 items-start" data-id="element-273">
              <div className="md:col-span-3" data-id="element-274">
                <h3 className={`text-xl font-bold uppercase tracking-wider reveal-hidden ${missionVisible ? 'animate-fade-right' : ''}`} data-id="element-275">
                  {t('aboutPage.mission.label')}
                </h3>
              </div>
              <div className="md:col-span-9 space-y-6" data-id="element-276">
                <h2 className={`text-2xl md:text-4xl font-bold leading-tight reveal-hidden ${missionVisible ? 'animate-fade-up delay-200' : ''}`} data-id="element-277">
                  {t('aboutPage.mission.heading')}
                </h2>
                <p className={`text-lg text-white/90 leading-relaxed reveal-hidden ${missionVisible ? 'animate-fade-up delay-300' : ''}`} data-id="element-278">
                  {t('aboutPage.mission.description')}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* 5. Our Vision */}
        <section className="py-24 bg-white text-[#061d76] relative overflow-hidden" ref={visionRef} data-id="element-279">
          <div className="container mx-auto px-6 md:px-12 relative z-10" data-id="element-280">
            <div className="grid md:grid-cols-12 gap-8 md:gap-12 items-start" data-id="element-281">
              <div className="md:col-span-3" data-id="element-282">
                <h3 className={`text-xl font-bold uppercase tracking-wider reveal-hidden ${visionVisible ? 'animate-fade-right' : ''}`} data-id="element-283">
                  {t('aboutPage.vision.label')}
                </h3>
              </div>
              <div className="md:col-span-9 space-y-6" data-id="element-284">
                <h2 className={`text-2xl md:text-4xl font-bold leading-tight reveal-hidden ${visionVisible ? 'animate-fade-up delay-200' : ''}`} data-id="element-285">
                  {t('aboutPage.vision.heading')}
                </h2>
                <p className={`text-lg text-[#061d76]/80 leading-relaxed reveal-hidden ${visionVisible ? 'animate-fade-up delay-300' : ''}`} data-id="element-286">
                  {t('aboutPage.vision.description')}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* 6. Join Our Bold Thinkers CTA */}
        <section className="relative py-24 text-white overflow-hidden" dir={direction} ref={ctaRef} data-id="element-287">
          {/* Background Image */}
          <div className="absolute inset-0 bg-cover bg-center" style={{
          backgroundImage: "url('https://cdn.magicpatterns.com/uploads/aNushftuwBdDBhG34PgQWF/Trusted.jpg')"
        }} data-id="element-288"></div>

          <div className="container mx-auto px-6 md:px-12 relative z-10" data-id="element-289">
            <div data-id="element-290">
              <h2 className={`text-4xl md:text-5xl font-bold mb-6 whitespace-pre-line reveal-hidden ${ctaVisible ? 'animate-fade-left' : ''}`} data-id="element-291">
                {t('aboutPage.cta.heading')}
              </h2>
              <p className={`text-white max-w-[950px] text-lg mb-8 leading-relaxed reveal-hidden ${ctaVisible ? 'animate-fade-left delay-200' : ''}`} data-id="element-292">
                {t('aboutPage.cta.description')}
              </p>
              <button className={`btn-primary reveal-hidden ${ctaVisible ? 'animate-fade-up delay-400' : ''}`} data-id="element-293">
                {t('aboutPage.cta.button')}
              </button>
            </div>
          </div>
        </section>
      </main>

      <Footer data-id="element-294" />
    </div>;
}